const { min } = Math;

/** Generate a function for progressively matching the given string.
 *
 * The returned function has three signatures:
 *
 * ```ts
 * // arbitrarily grab the next 'query' characters (or fewer if near/at the end)
 * match(query: number, peek?: boolean): string;
 *
 * // read the given string 'query' if the next characters match exactly
 * match(query: string, peek?: boolean): string | null;
 *
 * // match the given string against the given regex, from the current position (regex must start with ^)
 * match(query: RegExp, peek?: boolean): RegExpMatchArray | null;
 * ```
 *
 * By default, each call to the function moves the 'current read position' forward by the length matched.
 * If peek is `true`, however, the read position doesn't move when matching.
 *
 * Other properties:
 *
 * - `match.index`: The current character read position in the string.
 * - `match.source`: The original source string.
 * - `match.reverter`: A function that stores the read position when called and returns another function that reverts back to that position when called.
 */
export function parseString(string: string) {
  function match(query: number, peek?: boolean): string;
  function match(query: string, peek?: boolean): string | null;
  function match(query: RegExp, peek?: boolean): RegExpMatchArray | null;
  function match(
    query: string | number | RegExp,
    peek?: boolean
  ): string | RegExpMatchArray | null;

  function match(
    query: string | number | RegExp,
    peek: boolean = false
  ): string | RegExpMatchArray | null {
    // if numeric, read next 'query' chars
    if (typeof query === "number") {
      return string.slice(
        match.index,
        peek
          ? match.index + query
          : (match.index = min(match.index + query, string.length))
      );
    }

    // if string, read next text piece if it matches string
    if (typeof query === "string") {
      return string.slice(match.index, match.index + query.length) === query
        ? (peek || (match.index += query.length), query)
        : null;
    }

    // otherwise, query is a RegExp
    // validate RegExp and contents (RegExp MUST begin with ^)
    if (!query || ("" + query)[1] !== "^") {
      throw new Error("Unexpected query type or non-anchored regexp: " + query);
    }

    // match string to RegExp
    const m = string.slice(match.index).match(query);
    return m ? (peek || (match.index += m[0].length), m) : null;
  }

  match.index = 0;
  match.source = string;
  match.reverter = () => {
    const backTo = match.index;
    return (): false => {
      match.index = backTo;
      return false;
    };
  };

  return match;
}
